import React from 'react';

interface Dispatch<A> {
  (action: A): void;
}

function useSafeDispatch<A>(dispatch: Dispatch<A>) {
  const mounted = React.useRef(false);
  React.useLayoutEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return React.useCallback(
    (action: A) => (mounted.current ? dispatch(action) : void 0),
    [dispatch]
  );
}

function reportNonExhautiveCheck(x: never): never {
  throw new Error(`Non-exhaustive check: case ${x} was not handled.`);
}

enum AsyncStatusName {
  Idle = 'idle',
  Pending = 'pending',
  Rejected = 'rejected',
  Resolved = 'resolved',
}

interface AsyncError {
  message: string;
}

interface GenericAsyncOutput<T> {
  setData: (data: T) => void;
  setError: (error: AsyncError) => void;
  error: AsyncError | null;
  data: T | null;
  status: AsyncStatusName;
  isIdle: boolean;
  isPending: boolean;
  isLoading: boolean;
  isRejected: boolean;
  isError: boolean;
  isResolved: boolean;
  isSuccess: boolean;
  run: (p: Promise<T>) => Promise<T>;
  reset: () => void;
}

interface IdleAsyncOutput<T> extends GenericAsyncOutput<T> {
  status: AsyncStatusName.Idle;
  isIdle: true;
  isPending: false;
  isLoading: false;
  isRejected: false;
  isError: false;
  isResolved: false;
  isSuccess: false;
}

interface PendingAsyncOutput<T> extends GenericAsyncOutput<T> {
  status: AsyncStatusName.Pending;
  isIdle: false;
  isPending: true;
  isLoading: true;
  isRejected: false;
  isError: false;
  isResolved: false;
  isSuccess: false;
}

interface RejectedAsyncOutput<T> extends GenericAsyncOutput<T> {
  status: AsyncStatusName.Rejected;
  isIdle: false;
  isPending: false;
  isLoading: false;
  isRejected: true;
  isError: true;
  isResolved: false;
  isSuccess: false;
}

interface ResolvedAsyncOutput<T> extends GenericAsyncOutput<T> {
  status: AsyncStatusName.Resolved;
  isIdle: false;
  isPending: false;
  isLoading: false;
  isRejected: false;
  isError: false;
  isResolved: true;
  isSuccess: true;
}

export type AsyncOutput<T> =
  | IdleAsyncOutput<T>
  | PendingAsyncOutput<T>
  | RejectedAsyncOutput<T>
  | ResolvedAsyncOutput<T>;

interface State<T> {
  status: AsyncStatusName;
  data: T | null;
  error: AsyncError | null;
}

enum ActionName {
  Start = 'start',
  Resolve = 'resolve',
  Reject = 'reject',
  Reset = 'reset',
}

interface GenericAction {
  type: ActionName;
}

interface StartAction extends GenericAction {
  type: ActionName.Start;
}

interface RejectAction extends GenericAction {
  type: ActionName.Reject;
  data: null;
  error: AsyncError;
}

interface ResolveAction<T> extends GenericAction {
  type: ActionName.Resolve;
  data: T;
  error: null;
}

interface ResetAction<T> extends GenericAction {
  type: ActionName.Reset;
  data: T | null;
  error: AsyncError | null;
}

type Action<T> = StartAction | RejectAction | ResolveAction<T> | ResetAction<T>;

type AsyncReducer<T> = (prevState: State<T>, action: Action<T>) => State<T>;

function asyncReducer<T>(state: State<T>, action: Action<T>): State<T> {
  switch (action.type) {
    case ActionName.Start:
      return {
        ...state,
        status: AsyncStatusName.Pending,
      };
    case ActionName.Reject:
      return {
        status: AsyncStatusName.Rejected,
        data: null,
        error: action.error,
      };
    case ActionName.Resolve:
      return {
        status: AsyncStatusName.Resolved,
        data: action.data,
        error: null,
      };
    case ActionName.Reset:
      return {
        status: AsyncStatusName.Idle,
        data: action.data || null,
        error: action.error || null,
      };
    default: {
      return reportNonExhautiveCheck(action);
    }
  }
}

const defaultInitialState = {
  status: AsyncStatusName.Idle,
  data: null,
  error: null,
};

function useAsync<T>(initialState?: Partial<State<T>>): AsyncOutput<T> {
  const initialStateRef = React.useRef({
    ...defaultInitialState,
    ...initialState,
  });

  const [{ status, data, error }, setAsyncState] = React.useReducer<AsyncReducer<T>>(
    asyncReducer,
    initialStateRef.current
  );

  const safeSetState = useSafeDispatch(setAsyncState);

  const setData = React.useCallback(
    (newData: T) =>
      safeSetState({
        type: ActionName.Resolve,
        data: newData,
        error: null,
      }),
    [safeSetState]
  );
  const setError = React.useCallback(
    (newError: AsyncError) =>
      safeSetState({
        type: ActionName.Reject,
        error: newError,
        data: null,
      }),
    [safeSetState]
  );
  const reset = React.useCallback(
    () =>
      safeSetState({
        type: ActionName.Reset,
        ...initialStateRef.current,
      }),
    [safeSetState]
  );

  const run = React.useCallback(
    // @ts-ignore
    promise => {
      if (!promise || !promise.then) {
        throw new Error(`The argument passed to useAsync().run must be a promise`);
      }
      safeSetState({
        type: ActionName.Start,
        error: null,
      });
      return promise.then(
        (data: T) => {
          setData(data);
          return data;
        },
        (error: AsyncError) => {
          setError(error);
          return Promise.reject(error);
        }
      );
    },
    [safeSetState, setData, setError]
  );

  switch (status) {
    case AsyncStatusName.Idle:
      return {
        isIdle: true,
        isPending: false,
        isLoading: false,
        isRejected: false,
        isError: false,
        isResolved: false,
        isSuccess: false,
        setData,
        setError,
        error,
        status,
        data,
        run,
        reset,
      } as IdleAsyncOutput<T>;
    case AsyncStatusName.Pending:
      return {
        isIdle: false,
        isPending: true,
        isLoading: true,
        isRejected: false,
        isError: false,
        isResolved: false,
        isSuccess: false,
        setData,
        setError,
        error,
        status,
        data,
        run,
        reset,
      } as PendingAsyncOutput<T>;
    case AsyncStatusName.Rejected:
      return {
        isIdle: false,
        isPending: false,
        isLoading: false,
        isRejected: true,
        isError: true,
        isResolved: false,
        isSuccess: false,
        setData,
        setError,
        error,
        status,
        data,
        run,
        reset,
      } as RejectedAsyncOutput<T>;
    case AsyncStatusName.Resolved:
      return {
        isIdle: false,
        isPending: false,
        isLoading: false,
        isRejected: false,
        isError: false,
        isResolved: true,
        isSuccess: true,
        setData,
        setError,
        error,
        status,
        data,
        run,
        reset,
      } as ResolvedAsyncOutput<T>;
    default:
      return reportNonExhautiveCheck(status);
  }
}

export { useAsync };
export { useCanSeeDebug } from './useCanSeeDebug';
export { useTableDebug } from './useTableDebug';
export { useUnsavedChangesPrompt } from './useUnsavedChangesPrompt';
export { useIsGuest } from './useIsGuest';
